import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  contexto as api,
  type ContextoDaConversa,
  type ItemDaLinhaDoTempo,
  type LinhaDoTempoDoContato,
  type ResumoDoContato,
  type TurnoDoAssistente,
} from '../api/roteamento'
import { buscarAtendimento, type AtendimentoAcervo } from '../api/acervo'
import { useContextoChatwoot } from './useContextoChatwoot'
import { BarraDeAtualizacao } from './BarraDeAtualizacao'
import { AvaliacaoDoTurno } from './AvaliacaoDoTurno'
import { EstadoDaIa } from './EstadoDaIa'
import { EnvioDeModeloComArquivo } from './EnvioDeModeloComArquivo'
import { useFrescor } from './useFrescor'
import { formatar } from '../i18n'
import './historico.css'
import './contexto.css'

/**
 * O que a etiqueta de pausa deve dizer (demanda 130, sobre a 124).
 *
 * 🔴 **Três estados, e não dois.** É tentador reduzir a "pausada ou não" — mas
 * a ata de 27/08 pede exatamente a distinção que falta: *"se a IA parou de
 * responder por estar pausada ou por falha técnica"*.
 *
 * ⭐ E o terceiro estado — `naoSei` — é o que impede o defeito da 124 de voltar
 * pela tela. Conversa pausada antes daquela demanda, ou pausada pela trava
 * anti-laço, **não tem motivo registrado**; mostrá-la como "o cliente pediu"
 * seria repetir na interface a invenção que o resumo cometia.
 */
function estadoDaPausa(motivo: string | null): 'cliente' | 'falha' | 'regra' | 'naoSei' {
  switch (motivo) {
    case 'PedidoDoCliente':
      return 'cliente'

    // Falha nossa: é o que o atendente precisa ver DIFERENTE de tudo o mais,
    // porque é o único caso em que a conversa parou por defeito.
    case 'FalhaRepetida':
    case 'DegradacaoDaIa':
    case 'TravaDeSeguranca':
      return 'falha'

    case 'RegraDeNegocio':
    case 'ForaDeEscopo':
    case 'AssuntoDaEquipe':
    case 'ComprovanteDePagamento':
    case 'BaixaConfianca':
    case 'SemCoberturaNaTabela':
      return 'regra'

    default:
      return 'naoSei'
  }
}

/**
 * Painel de contexto embutido no Chatwoot (`P-38`).
 *
 * Responde à pergunta que o atendente faz ao assumir uma conversa: **quem é
 * este, o que já pedimos, o que já foi orçado, o que está a caminho.** Hoje ele
 * descobre isso rolando a conversa para cima — e o que estiver em outro canal
 * ele não descobre de jeito nenhum.
 *
 * Distinto do painel de histórico (`P-37`), que mostra o **acervo da Zintech**.
 * Este mostra o que aconteceu **no sistema novo**.
 */
export function ContextoConversa() {
  const { t } = useTranslation()
  const ctx = useContextoChatwoot()

  const [dados, setDados] = useState<ContextoDaConversa | null>()
  const [linha, setLinha] = useState<LinhaDoTempoDoContato | null>(null)
  const [resumo, setResumo] = useState<ResumoDoContato | null>()
  const [semContato, setSemContato] = useState(false)
  const [falhou, setFalhou] = useState(false)
  const frescor = useFrescor()

  useEffect(() => {
    // 🔴 Chave **ou** conversa (067). Exigir a chave — telefone ou identificador
    // do canal — deixava o painel mudo em todo contato de Telegram ou Instagram
    // sem número, que é justamente onde a operação está entrando. O Telegram não
    // fornece telefone, e quando o Chatwoot não preenche `identifier` a chave
    // fica vazia.
    //
    // O back responde por `conversaExterna` sozinho — conferido em produção. O
    // servidor sabia responder; o front é que não perguntava.
    //
    // A conversa entrou na 041 por ser MAIS precisa: a chave resolve o contato,
    // não qual conversa. O defeito foi ter feito dela um pré-requisito em vez de
    // uma das duas formas de perguntar.
    if (!ctx.chave && !ctx.conversaExterna) return

    setDados(undefined)
    setResumo(undefined)
    setSemContato(false)
    setFalhou(false)

    api
      .buscar(ctx.chave, ctx.conversaExterna)
      .then((r) => {
        // 204 vira `null`: o contato ainda não foi reconhecido. É diferente de
        // erro, e a tela precisa dizer isso — mostrar falha para um contato novo
        // faria o atendente desconfiar do painel em vez do dado.
        if (r === null) setSemContato(true)
        else setDados(r)
      })
      .catch(() => setFalhou(true))
      .finally(frescor.marcar)

    // 🔴 O resumo vem em separado, e é assim que os fatos aparecem primeiro. Se
    // a redação demora — ou não vem —, o painel já está útil.
    api
      .resumo(ctx.chave, ctx.conversaExterna)
      .then(setResumo)
      .catch(() => setResumo(null))

    // A linha do tempo vem em separado: ela reconcilia com o acervo e pode
    // demorar mais que o resto. Falhar aqui não pode derrubar o painel inteiro —
    // o contexto do portal continua útil sem o histórico migrado.
    api
      .linhaDoTempo(ctx.chave, ctx.conversaExterna)
      .then(setLinha)
      .catch(() => setLinha(null))
    // 🔴 `ctx.versao` entra aqui, e é a demanda 109 inteira do lado do painel.
    //
    // Ela sobe quando o atendente volta a esta aba, quando troca de conversa e
    // quando clica em atualizar. Sem ela, mensagem nova na MESMA conversa não
    // mudaria dependência nenhuma — o orçamento calculado há um minuto não
    // apareceria, e a tela continuaria parecendo certa.
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.chave, ctx.conversaExterna, ctx.versao])

  if (ctx.origem === 'aguardando') return <div className="aviso">{t('historico.aguardando')}</div>

  if (falhou) return <div className="aviso aviso--erro">{t('contexto.falhou')}</div>

  if (semContato)
    return (
      <div className="aviso">
        <p>{t('contexto.semContato')}</p>
        <p className="contexto__dica">{t('contexto.semContatoDica')}</p>
      </div>
    )

  if (!dados) return <div className="aviso">{t('comum.carregando')}</div>

  return (
    // `main`, e não `div`: dentro do iframe esta é a página inteira, e sem o
    // marco de referência o conteúdo fica fora de qualquer landmark — o axe
    // acusa, e quem navega por leitor de tela não tem por onde pular.
    <main className="contexto">
      <BarraDeAtualizacao em={frescor.em} />

      {/* 🔴 **A IA está atendendo? Se não, por quê e até quando** (ficha 310). No topo:
          é o que muda o que o atendente faz AGORA. Em 05/10 a IA ficou muda em duas
          conversas diante do cliente, e ninguém na sala sabia que era o silêncio de 12 h
          depois da fala de uma pessoa. */}
      <EstadoDaIa estado={dados.ia} conversaExterna={ctx.conversaExterna} />

      <header className="contexto__cabecalho">
        <h1 className="contexto__nome">{dados.contato.nome ?? t('contexto.semNome')}</h1>

        {/* "23 conversas" e as 10 últimas são informações diferentes, e a
            primeira é a que muda a postura de quem atende. */}
        <p className="contexto__resumo">
          {t('contexto.desde', { data: formatar.data(dados.contato.criadoEm) })}
          {' · '}
          {t('contexto.conversas', { total: dados.totalDeConversas })}
        </p>

        <ul className="contexto__identificadores">
          {dados.contato.identificadores.map((i) => (
            <li key={`${i.tipo}-${i.valor}`}>
              <span className="contexto__tipo">{t(`contexto.tipo${i.tipo}`, i.tipo)}</span>{' '}
              {i.valor}
            </li>
          ))}
        </ul>
      </header>

      <Resumo resumo={resumo} />

      <Secao titulo={t('contexto.orcamentos')} vazio={dados.orcamentos.length === 0}>
        <ul className="contexto__lista">
          {dados.orcamentos.map((o, posicao) => (
            // A chave é a posição: a referência da planilha NÃO é única (o mesmo
            // código serve a várias propostas) e o número interno não vem mais.
            <li key={`${posicao}-${o.calculadoEm}`} className="contexto__item">
              <p className="contexto__item-titulo">
                {/* 🔴 A referência da planilha, e nunca o número interno (292).
                    Sem referência, a tela DIZ que não há — um espaço vazio seria
                    lido como defeito, e o número interno no lugar é o defeito. */}
                {o.referencia?.trim() || t('contexto.semReferencia')} · {o.familia}
              </p>
              <p className="contexto__item-detalhe">
                {t('contexto.tiragem', { total: formatar.numero(o.tiragem) })} ·{' '}
                <strong>{formatar.moeda(o.total)}</strong> ·{' '}
                {formatar.data(o.calculadoEm)}
              </p>
            </li>
          ))}
        </ul>
      </Secao>

      <Secao titulo={t('contexto.conversasRecentes')} vazio={dados.conversas.length === 0}>
        <ul className="contexto__lista">
          {dados.conversas.map((c) => (
            <li key={c.id} className="contexto__item">
              <p className="contexto__item-titulo">
                {c.canal} · {c.marca}

                {/* 🔴 **A etiqueta diz POR QUE, não só que está pausada**
                    (demanda 130, sobre a 124).

                    Pausa por desenho e pausa por defeito tinham a mesma
                    aparência: silêncio. Quem atende precisava abrir o Contexto,
                    ler o detalhe e interpretar — e a ata de 27/08 pede
                    justamente que "os usuários saibam se a IA parou de responder
                    por estar pausada ou por falha técnica".

                    ⚠️ O ícone acompanha o texto porque cor não pode ser o único
                    portador de informação (UI-09). */}
                {c.pausada && (
                  <span
                    className={`contexto__etiqueta contexto__etiqueta--${estadoDaPausa(c.motivoDoTransbordoDaPausa)}`}
                  >
                    {t(`contexto.pausa.${estadoDaPausa(c.motivoDoTransbordoDaPausa)}`)}
                  </span>
                )}

                {/* 🔴 **As etiquetas do ciclo, que o painel nunca mostrou**
                    (demanda 167, observação O10 de 02/09).

                    Elas existem no domínio desde a 072, são gravadas e a régua
                    de follow-up as lê — e quem atende precisava procurá-las na
                    lateral do Chatwoot, onde a 159 mostrou que metade delas
                    está invisível ao filtro.

                    ⚠️ São de OPERAÇÃO, não estado da conversa: por isso vêm
                    depois da pausa e em tom neutro. A pausa diz por que a IA
                    calou; a etiqueta diz onde a conversa está no ciclo. */}
                {c.etiquetas?.map((e) => (
                  <span key={e} className="contexto__etiqueta contexto__etiqueta--ciclo">
                    {e}
                  </span>
                ))}
              </p>
              <p className="contexto__item-detalhe">
                {formatar.dataHora(c.ultimaAtividadeEm)} ·{' '}
                {t('contexto.mensagens', { total: c.mensagens })}
                {c.motivoDaPausa && ` · ${c.motivoDaPausa}`}
              </p>
            </li>
          ))}
        </ul>
      </Secao>

      {/* 🔴 **A avaliação fica ANTES dos transbordos e depois das conversas**
          (demanda 209). É posição, e é decisão: o atendente acaba de ler o que
          aconteceu, e é nesse instante que ele sabe dizer o que deveria ter
          acontecido. No fim do painel, depois de enviados e documentos, ela
          vira rodapé — e rodapé ninguém preenche.

          ⚠️ Só com conversa aberta ao lado: sem ela não há turno para ancorar,
          e um polegar sem âncora devolveria exatamente o "ruim" solto que este
          instrumento existe para substituir. */}
      {ctx.conversaExterna && <AvaliacaoDoTurno conversaExterna={ctx.conversaExterna} />}

      {/* Demanda 277: o modelo com arquivo, para a prova que sai fora da janela de
          24 h. ⛔ Aqui só o CAMINHO: o envio exige o login do portal (decisão do
          Ferrari, B2), e o botão abre a tela do portal numa aba. Sem checar
          permissão: a sessão do iframe nunca a tem, e quem decide é o portal. */}
      {ctx.conversaExterna && <EnvioDeModeloComArquivo />}

      <Secao titulo={t('contexto.transbordos')} vazio={dados.transbordos.length === 0}>
        <ul className="contexto__lista">
          {dados.transbordos.map((tr) => (
            <li key={`${tr.em}-${tr.motivo}`} className="contexto__item">
              <p className="contexto__item-titulo">
                {t(`transbordo.${tr.motivo}`, tr.motivo)} → {tr.time}
              </p>
              <p className="contexto__item-detalhe">{formatar.dataHora(tr.em)}</p>
            </li>
          ))}
        </ul>
      </Secao>

      <Secao titulo={t('contexto.enviados')} vazio={dados.enviados.length === 0}>
        {/* Só o que SAIU de fato. Envio pendente aqui faria o atendente supor
            que o cliente já recebeu — e responder em cima disso. */}
        <ul className="contexto__lista">
          {dados.enviados.map((e, i) => (
            <li key={`${e.criadoEm}-${i}`} className="contexto__item">
              <p className="contexto__item-titulo">{t(`contexto.envio${e.tipo}`, e.tipo)}</p>
              <p className="contexto__item-detalhe">
                {formatar.dataHora(e.criadoEm)} · {e.resumo}
              </p>
            </li>
          ))}
        </ul>
      </Secao>
      {linha && linha.total > 0 && (
        <section className="contexto__secao">
          <h2 className="contexto__secao-titulo">
            {t('linhaDoTempo.titulo', { total: linha.total })}
          </h2>

          {/* 🔴 A cobertura vem antes da lista, e não depois: sem saber a partir
              de quando existe histórico, quem lê conclui "cliente novo" de uma
              ausência que é da FONTE, não do cliente. */}
          {linha.cobertura && (
            <p className="contexto__dica">
              {t('linhaDoTempo.cobertura', { data: formatar.data(linha.cobertura) })}
            </p>
          )}

          {/* O cabeçalho responde "desde quando" e "por onde" antes da lista.
              O período vem da linha do tempo do CONTATO; a cobertura, acima, é
              da fonte — são perguntas diferentes e ficam separadas. */}
          {linha.primeira && linha.ultima && (
            <p className="linhaDoTempo__periodo">
              {t('linhaDoTempo.periodo', {
                primeira: formatar.data(linha.primeira),
                ultima: formatar.data(linha.ultima),
              })}
            </p>
          )}

          {linha.canais.length > 0 && (
            <p className="linhaDoTempo__canais">
              {t('linhaDoTempo.canais', { canais: linha.canais.join(' · ') })}
            </p>
          )}

          <ol className="linhaDoTempo">
            {linha.itens.map((i) => (
              <ItemDaLinha key={`${i.origem}-${i.referencia}`} item={i} />
            ))}
          </ol>
        </section>
      )}

      {linha?.canalNaoCasa && (
        <p className="contexto__dica">{t('linhaDoTempo.canalNaoCasa')}</p>
      )}

      <UltimoTurno chave={ctx.chave} conversaExterna={ctx.conversaExterna} />
    </main>
  )
}

/**
 * O resumo, **em cima dos fatos** (041 §3.1).
 *
 * 🔴 **O aviso não é rodapé jurídico.** O resumo é redação de modelo, e um
 * atendente copiando-o como fato para o cliente é a armadilha 13 com outro
 * sujeito — a diferença é que aqui quem repassa é uma pessoa, e ela acredita no
 * que leu. Por isso o aviso fica colado no texto, e não no fim da página.
 */
function Resumo({ resumo }: { resumo: ResumoDoContato | null | undefined }) {
  const { t } = useTranslation()

  // Carregando: os fatos já estão na tela; aqui só a redação falta.
  if (resumo === undefined) return <p className="contexto__dica">{t('contexto.resumoCarregando')}</p>

  // Falha da própria chamada. Sem a palavra "erro": o painel continua útil, e o
  // que o atendente precisa saber é que a redação não veio, não que algo quebrou.
  if (resumo === null) return <p className="contexto__dica">{t('contexto.resumoIndisponivel')}</p>

  if (resumo.semBase) return <p className="contexto__dica">{t('contexto.resumoSemBase')}</p>

  if (resumo.indisponivel) return <p className="contexto__dica">{t('contexto.resumoIndisponivel')}</p>

  if (!resumo.texto) return null

  return (
    <section className="contexto__secao contexto__resumo-ia">
      <h2 className="contexto__secao-titulo">{t('contexto.resumoTitulo')}</h2>

      <p className="contexto__resumo-texto">{resumo.texto}</p>

      <p className="contexto__aviso-fonte">{t('contexto.resumoNaoEFonte')}</p>
    </section>
  )
}

/**
 * O que o assistente recebeu — recolhido por padrão (041 §3.4).
 *
 * É diagnóstico, não decoração: quando o assistente responde algo estranho, o
 * atendente vê **o que ele tinha** em vez de supor. Recolhido porque mostra a
 * conversa inteira, e porque só é buscado quando alguém abre.
 */
function UltimoTurno({ chave, conversaExterna }: { chave?: string; conversaExterna?: number }) {
  const { t } = useTranslation()

  const [aberto, setAberto] = useState(false)
  const [turno, setTurno] = useState<TurnoDoAssistente | null>()

  function alternar() {
    if (aberto) return setAberto(false)

    setAberto(true)

    if (turno !== undefined) return

    api
      .ultimoTurno(chave, conversaExterna)
      .then(setTurno)
      .catch(() => setTurno(null))
  }

  return (
    <section className="contexto__secao">
      <button
        type="button"
        className="contexto__revelar"
        aria-expanded={aberto}
        onClick={alternar}
      >
        {t('contexto.turnoTitulo')}
      </button>

      {aberto && turno === undefined && (
        <p className="contexto__dica">{t('comum.carregando')}</p>
      )}

      {aberto && turno === null && <p className="contexto__dica">{t('contexto.turnoFalhou')}</p>}

      {aberto && turno && !turno.existe && (
        <p className="contexto__dica">{t('contexto.turnoSemConversa')}</p>
      )}

      {aberto && turno?.existe && (
        <>
          <p className="contexto__dica">
            {t('contexto.turnoMontadoAgora', {
              assistente: turno.assistente,
              modelo: turno.modelo,
            })}
          </p>

          {turno.versaoDoPrompt !== null && (
            <p className="contexto__dica">
              {t('contexto.turnoVersao', { versao: turno.versaoDoPrompt })}
              {turno.promptMudouDepois && ` · ${t('contexto.turnoPromptMudou')}`}
            </p>
          )}

          <ol className="contexto__turno">
            {turno.contexto.map((m, i) => (
              <li key={i} className={`contexto__turno-item contexto__turno-item--${m.papel.toLowerCase()}`}>
                <span className="contexto__turno-papel">
                  {t(`contexto.papel${m.papel}`, m.papel)}
                </span>
                <pre className="contexto__turno-texto">{m.texto}</pre>
              </li>
            ))}
          </ol>
        </>
      )}
    </section>
  )
}

/**
 * Uma linha da linha do tempo, e o que acontece ao clicar nela.
 *
 * 🔴 **As duas origens abrem de jeitos diferentes, e não é inconsistência.**
 * A conversa do **portal** vive na plataforma de atendimento e abre em nova aba,
 * onde o atendente já está logado. O atendimento do **acervo** abre *aqui
 * dentro*: o painel de histórico depende do `postMessage` que entrega o contato
 * e de um cookie `Partitioned` — nenhum dos dois sobrevive a uma aba de topo, e
 * o link levaria a uma tela pedindo login.
 */
function ItemDaLinha({ item }: { item: ItemDaLinhaDoTempo }) {
  const { t } = useTranslation()

  const [aberto, setAberto] = useState(false)
  const [detalhe, setDetalhe] = useState<AtendimentoAcervo | null>()
  const [falhou, setFalhou] = useState(false)

  const resumo = (
    <>
      <span className={`linhaDoTempo__origem linhaDoTempo__origem--${item.origem.toLowerCase()}`}>
        {t(`linhaDoTempo.origem.${item.origem.toLowerCase()}`)}
      </span>
      <span className="linhaDoTempo__data">{formatar.data(item.em)}</span>
      {item.canal && <span className="linhaDoTempo__canal">{item.canal}</span>}
      <span className="linhaDoTempo__assunto">{item.assunto}</span>
    </>
  )

  if (item.origem === 'Portal') {
    // Sem link configurado a linha continua legível — só não é clicável. Melhor
    // que uma âncora morta, que promete e não cumpre.
    if (!item.link) return <li className="linhaDoTempo__item">{resumo}</li>

    return (
      <li className="linhaDoTempo__item">
        <a
          className="linhaDoTempo__abrir"
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          title={t('linhaDoTempo.abrirNoPortal')}
        >
          {resumo}
        </a>
      </li>
    )
  }

  function alternar() {
    if (aberto) return setAberto(false)

    setAberto(true)

    if (detalhe !== undefined) return

    setFalhou(false)

    buscarAtendimento(item.referencia)
      .then(setDetalhe)
      .catch(() => setFalhou(true))
  }

  return (
    <li className="linhaDoTempo__item">
      <button
        type="button"
        className="linhaDoTempo__abrir"
        aria-expanded={aberto}
        onClick={alternar}
        title={t(aberto ? 'linhaDoTempo.fecharDoAcervo' : 'linhaDoTempo.abrirDoAcervo')}
      >
        {resumo}
      </button>

      {aberto && falhou && <p className="contexto__dica">{t('linhaDoTempo.falhouAtendimento')}</p>}

      {aberto && !falhou && detalhe === undefined && (
        <p className="contexto__dica">{t('linhaDoTempo.carregandoAtendimento')}</p>
      )}

      {aberto && detalhe && (
        <div className="conversa">
          {detalhe.mensagens.map((m) => (
            <div
              key={m.id}
              className={['balao', m.tipo === 'ATENDENTE' ? 'nosso' : 'deles'].join(' ')}
            >
              <p>{m.texto || <i>{t('historico.semTexto', { tipo: m.tipoMensagem ?? 'mídia' })}</i>}</p>
              <time>
                {m.autor ? `${m.autor} · ` : ''}
                {m.em && formatar.dataHora(m.em)}
              </time>
            </div>
          ))}
        </div>
      )}
    </li>
  )
}

function Secao({
  titulo,
  vazio,
  children,
}: {
  titulo: string
  vazio: boolean
  children: React.ReactNode
}) {
  const { t } = useTranslation()

  return (
    <section className="contexto__secao">
      <h2 className="contexto__secao-titulo">{titulo}</h2>
      {vazio ? <p className="contexto__vazio">{t('contexto.nada')}</p> : children}
    </section>
  )
}
